import {Alert, Backdrop, Box, CircularProgress, Typography} from '@mui/material';
import {useCallback, useEffect, useState} from 'react';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {useConfiguration} from '../../../hooks/configuration';
import {useAppSelector} from '../../../hooks/redux';
import {useRefreshInterval} from '../../../hooks/refreshInterval';
import {DashboardPanelData} from '../../../interfaces/Dashboard';
import {SafeyeNodeListResponse} from '../../../interfaces/SafeyeNode';
import {getMsFromS} from '../../../utils/datetime';

interface Props {
  value?: DashboardPanelData;
  onUpdate?: (value?: DashboardPanelData) => void;
}

export const HazardAIActiveVehicles = (_props?: Props) => {
  const machineTypes = useAppSelector(({assets}) => assets.machine_types);

  const [fetchedData, setFetchedData] = useState<SafeyeNodeListResponse>();
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);

  const fetchData = useCallback(async () => {
    setFetchedInProgress(true);
    setFetchedErrors([]);
    try {
      const resp = await API.get<SafeyeNodeListResponse>(
        `${apiBaseUrl}/safeye-nano-node`,
        {params: {status: 'active', page: 0, limit: 5000}}
      );
      setFetchedData(resp.data);
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, 'STRA_e1c4a9d2');
      setFetchedErrors(messages);
    }
    setFetchedInProgress(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refreshConfig = useConfiguration('hazard-ai', 'hazard_ai_autorefresh_rate');
  const intervalMs = refreshConfig?.value ? getMsFromS(+refreshConfig.value) : null;
  useRefreshInterval(fetchData, intervalMs);

  // Build summary: distinct sites + per-type machine count
  const items = fetchedData?.items ?? [];
  const siteCount = new Set(items.map((n) => n.site_id)).size;

  const typeCounts: Record<number, number> = {};
  for (const node of items) {
    typeCounts[node.type_id] = (typeCounts[node.type_id] ?? 0) + 1;
  }

  const siteLabel = siteCount === 1 ? '1 site' : `${siteCount} sites`;
  const typeSegments = Object.entries(typeCounts).map(([typeIdStr, count]) => {
    const typeId = Number(typeIdStr);
    const typeName =
      machineTypes.find(({id}) => id === typeId)?.name ?? 'Unknown';
    return `${count} ${typeName}`;
  });
  const summaryParts = siteCount > 0 ? [siteLabel, ...typeSegments] : typeSegments;
  const summaryLine = summaryParts.join(' · ');

  const total = fetchedData?.count ?? items.length;

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="calc(100% - 38px)"
      width="100%"
      overflow="hidden"
    >
      <Backdrop open={fetchedInProgress} sx={{position: 'absolute'}}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {fetchedErrors.map((error, idx) => (
        <Alert key={idx} severity="error" onClose={fetchData}>
          {error}
        </Alert>
      ))}

      {/* KPI Content */}
      <Box
        display="flex"
        flexDirection="column"
        flex={1}
        overflow="auto"
        px={2}
        py={1}
        gap={0.5}
      >
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{letterSpacing: 2}}
        >
          / Active Vehicles
        </Typography>

        <Typography
          variant="h2"
          fontWeight={600}
          lineHeight={1.1}
          sx={{fontSize: {xs: '2.5rem', md: '3.5rem'}}}
        >
          {fetchedData !== undefined ? total.toLocaleString() : '—'}
        </Typography>

        {summaryLine && (
          <Typography variant="body2" color="text.secondary">
            {summaryLine}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
